import { InteractionRequiredAuthError, IPublicClientApplication } from "@azure/msal-browser";

const NON_PROD_API_SCOPES = ["api://1aaf894a-0fdb-487c-ace5-cc90e423ff5c/.default"];
const PROD_API_SCOPES = ["api://fc8c87b7-2b16-4641-b29d-f8e668c9838e/.default"];
const GRAPH_SCOPES = ["https://graph.microsoft.com/.default"];
const SPA_SCOPES = ["api://92ab6cff-3584-42fe-9d7f-cc77cc0dc897/user_impersonation"];
const INTERACTION_ATTEMPT_PREFIX = "nebula.msal.interactionAttempt.";
const INTERACTION_ATTEMPT_TTL_MS = 5 * 60 * 1000;

export class AuthClientProvider {
  private static instance: AuthClientProvider;
  private interactionRedirect?: Promise<void>;
  private readonly msalClient: IPublicClientApplication;

  private constructor(msalClient: IPublicClientApplication) {
    this.msalClient = msalClient;
  }

  public static configure(msalClient: IPublicClientApplication) {
    AuthClientProvider.instance = new AuthClientProvider(msalClient);
  }

  public static getInstance() {
    if (!AuthClientProvider.instance) {
      throw new Error("AuthClientProvider has not been configured.");
    }

    return AuthClientProvider.instance;
  }

  public getAuthToken = async (env = "none", forceRefresh = false) => {
    const selectedEnv = env === "none" ? window.localStorage.getItem("env") : env;
    const scopes = selectedEnv === "prod" ? PROD_API_SCOPES : NON_PROD_API_SCOPES;
    const token = await this.acquireToken(scopes, forceRefresh);
    return `Bearer ${token}`;
  };

  public getAuthToken_releaseManagement = async (environment: string) => {
    return this.getAuthToken(environment);
  };

  public getGraphAuthToken = async () => {
    const token = await this.acquireToken(GRAPH_SCOPES);
    return `Bearer ${token}`;
  };

  public get_SPA_AuthToken = async () => {
    const token = await this.acquireToken(SPA_SCOPES);
    return `Bearer ${token}`;
  };

  private acquireToken = async (scopes: string[], forceRefresh = false) => {
    const account = this.msalClient.getActiveAccount() ?? this.msalClient.getAllAccounts()[0];
    if (!account) {
      throw new Error("No active account. Verify that a user has signed in.");
    }

    if (!this.msalClient.getActiveAccount()) {
      this.msalClient.setActiveAccount(account);
    }

    try {
      const response = await this.msalClient.acquireTokenSilent({
        scopes,
        account,
        ...(forceRefresh && { forceRefresh: true }),
      });
      window.sessionStorage.removeItem(this.getInteractionAttemptKey(scopes));
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const interactionAttemptKey = this.getInteractionAttemptKey(scopes);
        const lastAttempt = Number(window.sessionStorage.getItem(interactionAttemptKey));
        if (Number.isFinite(lastAttempt) && Date.now() - lastAttempt < INTERACTION_ATTEMPT_TTL_MS) {
          throw error;
        }

        if (!this.interactionRedirect) {
          window.sessionStorage.setItem(interactionAttemptKey, Date.now().toString());
          this.interactionRedirect = this.msalClient
            .acquireTokenRedirect({ scopes, account })
            .catch((redirectError) => {
              window.sessionStorage.removeItem(interactionAttemptKey);
              throw redirectError;
            })
            .finally(() => {
              this.interactionRedirect = undefined;
            });
        }
        await this.interactionRedirect;
      }
      throw error;
    }
  };

  private getInteractionAttemptKey = (scopes: string[]) => {
    return `${INTERACTION_ATTEMPT_PREFIX}${scopes.join("|")}`;
  };
}
